import React, { useState, useEffect, useRef } from 'react';
import { X, HelpCircle, Users, CreditCard, ChevronRight, Check, CheckCircle, AlertCircle, Tag, Mail, User, Shield, Zap, Heart, MapPin, Calendar, Phone, Activity, Target, Link as LinkIcon, Bell, Smartphone, ExternalLink, Globe, Gift, Lock } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import { useReferral } from '../contexts/ReferralContext';
import { familyAPI, userAPI, accountAPI, revenueCatAPI, teamCodeAPI, guardianAPI } from '../services/api';
import ContactSupportModal from './ContactSupportModal';
import NotificationPreferencesModal from './NotificationPreferencesModal';
import EmailTemplatesModal from './EmailTemplatesModal';
import XTagListsModal from './XTagListsModal';
import SocialConnectPanel from './SocialConnectPanel';
import ConfirmDialog from './ConfirmDialog';
import ErrorState from './ErrorState';
import Modal from './Modal';
import { Button, Field, TextInput, fieldAria } from './ui';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  configureRevenueCat,
  getOfferings,
  purchasePackage,
  preparePurchase,
  type Package
} from '../services/revenueCatService';
import { REVENUECAT_CONFIG } from '../config/revenuecatConfig';

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Cancel link for web subscribers. The Stripe portal only manages store 'stripe' subscriptions.
const manageSubscriptionUrl = (sub: any, email?: string | null): string => {
  if (sub?.managementUrl) return sub.managementUrl;
  if (sub?.store === 'stripe') {
    return `https://checkout.apsrecruits.com/p/login/7sYaEXd6p9xIfGJ70ca7C00?prefilled_email=${encodeURIComponent(email || '')}`;
  }
  return 'mailto:support@apsrecruits.com?subject=Cancel%20my%20APS%20subscription';
};

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken, user, logout, setUser } = useAuth();
  const { family, plans, pendingInvitations, loadFamily, loadPlans, updateFamily, inviteMember, loadPendingInvitations, resendInvitation, cancelInvitation, removeMember } = useFamily();
  const toast = useToast();
  const { isMobile, isTablet } = useResponsive();
  const { referralInfo, validateCode: validateReferralCode, applyCode: applyReferralCode, removeCode: removeReferralCode, isLoading: referralLoading, refreshReferralInfo } = useReferral();
  const { requirePaid } = useSubscriptionGuard();
  // App-wide paid state — refreshed when prepare-purchase reports that a team
  // grant was just synced to RevenueCat (TEAM_COVERED + rcSynced).
  const { refetch: refetchSubscription } = useSubscription();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [contactSupportModalOpen, setContactSupportModalOpen] = useState(false);
  const [notificationPreferencesModalOpen, setNotificationPreferencesModalOpen] = useState(false);
  const [emailTemplatesModalOpen, setEmailTemplatesModalOpen] = useState(false);
  const [xTagListsModalOpen, setXTagListsModalOpen] = useState(false);
  const [memberDetailsModalOpen, setMemberDetailsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  
  // Family settings
  const [familyName, setFamilyName] = useState('');
  const [familyNameError, setFamilyNameError] = useState('');
  
  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  // True once loadSubscriptionData has finished (success or failure). Until
  // then we don't know whether the family pays, so the plan grid stays hidden
  // rather than flashing a Subscribe button at paying members.
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  
  // RevenueCat state
  const [revenueCatPackages, setRevenueCatPackages] = useState<Package[]>([]);
  const [revenueCatConfigured, setRevenueCatConfigured] = useState(false);
  
  // Coupon/Promo code state (for RevenueCat promo codes)
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralCodeLoading, setReferralCodeLoading] = useState(false);
  const [referralCodeError, setReferralCodeError] = useState<string | null>(null);
  const [referralValidationState, setReferralValidationState] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [showReferralChangeInput, setShowReferralChangeInput] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  
  // Promo cancellation confirmation state (when user has paid subscription)
  const [promoCancelConfirmModalOpen, setPromoCancelConfirmModalOpen] = useState(false);
  const [pendingPromoRedemption, setPendingPromoRedemption] = useState<{
    code: string;
    planTier: 'regular'; // promos always grant regular (audit #213); one plan
    confirmationMessage: string;
    currentSubscription: {
      tier: string;
      store: string;
      productId: string;
      expirationDate: string;
      willRenew: boolean;
    };
  } | null>(null);
  
  // Invitation state. Role picker dropped — the invitee picks during their
  // flow anyway and the inviter's pick was never enforced. Resend / cancel
  // states are inline (per-invitation IDs) so multiple rows can have an
  // action in-flight without confusing each other.
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  // ConfirmDialog targets — null when no dialog is showing.
  const [pendingCancelInvite, setPendingCancelInvite] = useState<{ id: string; email: string } | null>(null);
  const [pendingRemoveMember, setPendingRemoveMember] = useState<{ id: string; email: string } | null>(null);
  
  // Family members state
  const [detailedFamily, setDetailedFamily] = useState<any>(null);
  const [familyMembersError, setFamilyMembersError] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  // Your name. The signed-in user's own guardian profile, if they have one.
  // A non-guardian login counts only when its name matches the profile.
  const normName = (n?: string | null) => (n || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const myGuardian = detailedFamily?.guardians?.find((g: any) =>
    g.linked_users?.some((u: any) => u.id === user?.id) && (
      user?.user_type === 'guardian' ||
      normName(`${g.first_name || ''} ${g.last_name || ''}`) === normName(user?.name)
    )
  ) || null;
  const [myFirstName, setMyFirstName] = useState('');
  const [myLastName, setMyLastName] = useState('');
  const [myNameErrors, setMyNameErrors] = useState<{ first?: string; last?: string }>({});
  const [myNameSaveError, setMyNameSaveError] = useState<string | null>(null);
  const [savingMyName, setSavingMyName] = useState(false);
  useEffect(() => {
    if (!isOpen || !myGuardian) return;
    setMyFirstName(myGuardian.first_name || '');
    setMyLastName(myGuardian.last_name || '');
    setMyNameErrors({});
    setMyNameSaveError(null);
    // Refill only when the saved name changes, so typing is kept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, myGuardian?.id, myGuardian?.first_name, myGuardian?.last_name]);
  const isFamilyNameDirty = !!family && familyName !== (family.name || '');
  const isMyNameDirty = !!myGuardian && (
    myFirstName !== (myGuardian.first_name || '') || myLastName !== (myGuardian.last_name || '')
  );

  // Account deletion state
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Connections section (folds the standalone /settings/connections page into
  // Settings). Resolve the athlete id the same way that page does — through
  // getMyAthletes, so guardians get their linked athlete.
  const [connectionsAthleteId, setConnectionsAthleteId] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen || !sessionToken) return;
    let cancelled = false;
    (async () => {
      try {
        const my = await familyAPI.getMyAthletes(sessionToken);
        const first = my.athletes?.[0];
        if (!cancelled && first?.id) setConnectionsAthleteId(first.id);
      } catch (err) {
        console.error('AccountSettingsModal: failed to resolve connections athleteId', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, sessionToken]);
  
  // Team code state (per-athlete)
  const [athleteTeams, setAthleteTeams] = useState<Record<string, any[]>>({});
  const [athleteTeamCode, setAthleteTeamCode] = useState<Record<string, string>>({});
  const [athleteTeamCodeError, setAthleteTeamCodeError] = useState<Record<string, string>>({});
  const [athleteJoiningTeam, setAthleteJoiningTeam] = useState<Record<string, boolean>>({});
  
  // Manage via portal confirmation state. 'team_covered' = prepare-purchase
  // answered TEAM_COVERED for a family that also pays through a live store:
  // nothing to buy, but offer the store's manage link so they can stop paying.
  // (The old 'upgrade' plan-change use is gone — one plan, nothing to change to.)
  const [managePortalConfirmOpen, setManagePortalConfirmOpen] = useState(false);
  const [pendingManagePortal, setPendingManagePortal] = useState<{
    managementUrl: string;
    message: string;
    action: 'promo' | 'team_covered';
  } | null>(null);
  
  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);

  // Check if current user is billing owner
  const isBillingOwner = userProfile?.is_billing_owner ?? false;

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && sessionToken) {
      initializeData();
    }
  }, [isOpen, sessionToken]);

  // Set family name when family data loads
  useEffect(() => {
    if (family?.name) {
      setFamilyName(family.name);
    }
  }, [family]);

  // Configure RevenueCat when family is loaded
  useEffect(() => {
    const setupRevenueCat = async () => {
      if (family?.id && isOpen && !revenueCatConfigured) {
        try {
          await configureRevenueCat(family.id);
          setRevenueCatConfigured(true);
          
          // Load RevenueCat offerings/packages
          await loadRevenueCatOfferings();
          
          // Now load subscription data from RevenueCat
          await loadSubscriptionData();
        } catch (error) {
          console.error('Failed to configure RevenueCat:', error);
        }
      }
    };
    
    setupRevenueCat();
  }, [family?.id, isOpen]);

  // Set current plan as selected when subscription loads
  useEffect(() => {
    if (subscriptionData && plans && !selectedPlan) {
      const currentPlanId = getCurrentPlanId();
      if (currentPlanId) {
        setSelectedPlan(currentPlanId);
      }
    }
  }, [subscriptionData, plans]);

  // One plan (Premium retired 2026-09-11): when the plan grid shows (no
  // membership yet, or a promo to convert), preselect the APS Membership for
  // the billing owner so the Subscribe button is there without first clicking
  // the only card. (getCurrentPlanId finds no match for the single plan, so
  // the effect above never selects it.) Non-owners can't subscribe, so they
  // get no selection and no dead button.
  // KGH-790: a family covered by a live team has nothing to buy (team access
  // is lifetime while the team is active, and prepare-purchase refuses the
  // purchase with TEAM_COVERED), so it never gets the plan grid. A lifetime
  // comp (e.g. FREEPLAN) may still switch to paid, but nothing is pushed at
  // it: no preselected Subscribe button. If promo details failed to load we
  // fall back to showing the grid — the backend still guards the purchase.
  const promoInfo = subscriptionData?.promoDetails;
  const teamCovered = !!promoInfo?.teamCode;
  const lifetimeComp = !!promoInfo && !promoInfo.teamCode && !!promoInfo.isLifetime;
  const planGridShows = subscriptionLoaded && !teamCovered &&
    (!(subscriptionData?.status === 'active') || !!subscriptionData?.isPromotional);
  const preselectPlan = planGridShows && !lifetimeComp;

  useEffect(() => {
    if (!isOpen || !isBillingOwner || selectedPlan) return;
    const onlyPriceId = plans?.length === 1 ? plans[0].prices?.[0]?.id : undefined;
    if (preselectPlan && onlyPriceId) {
      setSelectedPlan(onlyPriceId);
    }
  }, [isOpen, isBillingOwner, selectedPlan, preselectPlan, plans]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const initializeData = async () => {
    setIsLoading(true);
    try {
      // First, load the family data
      await loadFamily();
      
      const [, , detailedFamilyResult] = await Promise.all([
        loadPlans(),
        loadUserProfile(),
        loadDetailedFamily(),
        loadPendingInvitations(),
      ]);

      // Load teams per athlete after we have the detailed family
      if (detailedFamilyResult?.athletes) {
        await Promise.all(
          detailedFamilyResult.athletes.map((a: any) => loadMyTeams(a.id))
        );
      }
    } catch (error) {
      console.error('Error initializing account settings:', error);
      toast.error('Failed to load account settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Load RevenueCat offerings and packages
  const loadRevenueCatOfferings = async () => {
    try {
      const offerings = await getOfferings();
      if (offerings.current?.availablePackages) {
        setRevenueCatPackages(offerings.current.availablePackages);
      }
    } catch (error) {
      console.error('Failed to load RevenueCat offerings:', error);
    }
  };

  const resetState = () => {
    setSubscriptionLoaded(false);
    setFamilyName('');
    setFamilyNameError('');
    setSelectedPlan(null);
    setProcessing(false);
    setCouponCode('');
    setCouponError(null);
    setInviteEmail('');
    setSendingInvite(false);
    setContactSupportModalOpen(false);
    setNotificationPreferencesModalOpen(false);
    setMemberDetailsModalOpen(false);
    setSelectedMember(null);
    setShowPaymentForm(false);
    setPaymentClientSecret(null);
    // Reset RevenueCat state
    setRevenueCatPackages([]);
    setRevenueCatConfigured(false);
  };

  const loadSubscriptionData = async () => {
    if (!family?.id || !sessionToken) {
      return;
    }
    try {
      // Get subscription status from our BACKEND (source of truth)
      // This properly detects all subscription types: Apple, Google, RC Billing, and Promotional
      const status = await revenueCatAPI.getSubscriptionStatus(sessionToken);
      
      if (status && status.isActive) {
        // Fetch promo details if available (for any subscription type)
        // This will return hasPromo: true only for promotional subscriptions
        let promoDetails = null;
        try {
          promoDetails = await revenueCatAPI.getPromoDetails(sessionToken);
        } catch (e) {
          console.warn('Failed to fetch promo details:', e);
        }

        // Transform backend data to match existing subscriptionData structure
        setSubscriptionData({
          status: 'active',
          plan: {
            name: status.productId || status.tier,
            tier: status.tier,
            // Map tier to package ID for compatibility. One plan (Premium
            // retired 2026-09-11): a legacy premium grant maps to no package.
            price_id: status.tier === 'regular'
              ? REVENUECAT_CONFIG.packages.regular
              : null,
          },
          current_period_end: status.expirationDate,
          expiresAt: status.expirationDate,
          willRenew: status.willRenew,
          // Track store for UI display
          store: status.store,
          tier: status.tier,
          managementUrl: status.managementUrl,
          isPromotional: status.store === 'promotional',
          // Include promo details if available
          promoDetails: promoDetails?.hasPromo ? {
            code: promoDetails.code,
            description: promoDetails.description,
            isLifetime: promoDetails.isLifetime,
            durationDays: promoDetails.durationDays,
            expiresAt: promoDetails.expiresAt,
            redeemedAt: promoDetails.redeemedAt,
            discountType: promoDetails.discountType,
            discountValue: promoDetails.discountValue,
            teamCode: promoDetails.teamCode,
          } : null,
        });
      } else {
        setSubscriptionData(null);
      }
      
      // Also configure RevenueCat Web SDK for purchases (but not for status)
      if (!revenueCatConfigured) {
        await configureRevenueCat(family.id);
        setRevenueCatConfigured(true);
      }
    } catch (err) {
      console.warn('Failed to load subscription data:', err);
      setSubscriptionData(null);
    } finally {
      setSubscriptionLoaded(true);
    }
  };

  const loadUserProfile = async () => {
    if (!sessionToken) return;
    try {
      const profile = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);
      
      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === profile.id
        );
        if (familyMember) {
          setUserProfile(familyMember);
        }
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  const loadDetailedFamily = async () => {
    if (!sessionToken) return null;
    setFamilyMembersError(false);
    try {
      const response = await familyAPI.getDetailedFamily(sessionToken);
      if (response.family) {
        setDetailedFamily(response.family);
        return response.family;
      }
      // No family payload — surface an error rather than spinning forever (#182).
      setFamilyMembersError(true);
    } catch (error) {
      console.error('Failed to load detailed family:', error);
      setFamilyMembersError(true);
    }
    return null;
  };

  const getCurrentPlanId = () => {
    if (!subscriptionData?.plan) return null;
    
    // For RevenueCat subscriptions, match by tier name
    if (subscriptionData.plan.tier) {
      const tierLower = subscriptionData.plan.tier.toLowerCase();
      const matchingPlan = plans?.find(plan => 
        plan.name?.toLowerCase().includes(tierLower)
      );
      if (matchingPlan?.prices?.[0]?.id) {
        return matchingPlan.prices[0].id;
      }
    }
    
    // Legacy: try price_id directly
    if (subscriptionData.plan.price_id) {
      // Check if this price_id exists in plans
      const planWithPriceId = plans?.find(plan =>
        plan.prices?.some((price: any) => price.id === subscriptionData.plan.price_id)
      );
      if (planWithPriceId) {
        return subscriptionData.plan.price_id;
      }
    }
    
    // Legacy: match by amount/currency/interval
    const originalAmount = subscriptionData.plan.amount;
    if (originalAmount !== undefined) {
      const currentPlan = plans?.find(plan =>
        plan.prices?.some((price: any) =>
          price.amount === originalAmount &&
          price.currency === subscriptionData.plan?.currency &&
          price.interval === subscriptionData.plan?.interval
        )
      );
      return currentPlan?.prices?.[0]?.id || null;
    }
    
    return null;
  };

  const formatPrice = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getCurrentDiscount = () => {
    if (!subscriptionData?.discount) return null;
    return subscriptionData.discount;
  };

  // Single-step promo code apply (validates and redeems in one action)
  const handleApplyPromoCode = async () => {
    if (!couponCode.trim() || !sessionToken) return;

    setCouponLoading(true);
    setCouponError(null);

    try {
      // audit #213: promo codes always grant the 'regular' tier (owner decision);
      // the tier is intentionally fixed, not derived from the code.
      const result = await revenueCatAPI.redeemPromoCode(sessionToken, couponCode.trim(), 'regular');

      if (result.success) {
        setCouponCode('');
        toast.success(result.message || 'Promo code applied successfully!');
        await loadSubscriptionData();
      } else {
        setCouponError(result.message || result.error || 'Invalid or expired promo code');
        toast.error(result.message || 'Invalid promo code');
      }
    } catch (error: any) {
      console.error('Error applying promo code:', error);
      setCouponError(error.message || 'Failed to apply promo code');
      toast.error(error.message || 'Failed to apply promo code');
    } finally {
      setCouponLoading(false);
    }
  };

  // Handler for applying referral codes
  const handleApplyReferralCode = async () => {
    if (!referralCode.trim()) return;

    setReferralCodeLoading(true);
    setReferralCodeError(null);

    try {
      // First validate the code
      const validationResult = await validateReferralCode(referralCode.trim());
      
      if (!validationResult.valid) {
        setReferralValidationState('invalid');
        setReferralCodeError(validationResult.error || 'Invalid or expired referral code');
        return;
      }

      // Then apply the code
      const result = await applyReferralCode(referralCode.trim(), 'settings');
      
      if (result.success) {
        setReferralValidationState('valid');
        setReferralCode('');
        toast.success(result.message || 'Referral code applied successfully!');
        await refreshReferralInfo();
      } else {
        setReferralValidationState('invalid');
        setReferralCodeError(result.message || 'Failed to apply referral code');
      }
    } catch (error: any) {
      console.error('Error applying referral code:', error);
      setReferralValidationState('invalid');
      setReferralCodeError(error.message || 'Failed to apply referral code');
    } finally {
      setReferralCodeLoading(false);
    }
  };

  // Handler for removing referral code
  const handleRemoveReferralCode = async () => {
    setReferralCodeLoading(true);
    try {
      const result = await removeReferralCode();
      if (result.success) {
        toast.success('Referral code removed successfully');
        setShowRemoveConfirm(false);
        setShowReferralChangeInput(false);
        await refreshReferralInfo();
      } else {
        toast.error(result.message || 'Failed to remove referral code');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove referral code');
    } finally {
      setReferralCodeLoading(false);
    }
  };

  const isSelectedPlanFree = () => {
    if (!selectedPlan) return false;
    const plan = plans?.find(p => p.prices?.[0]?.id === selectedPlan);
    return plan?.prices?.[0]?.amount === 0;
  };

  // One plan (Premium retired 2026-09-11): the plan grid only ever STARTS the
  // APS Membership (no member / promo converting to paid) — there is no other
  // plan to upgrade or downgrade to, so only the new-subscription copy remains.
  const getChangeDescription = (newPriceId: string) => {
    if (!plans) return '';
    const newPlan = plans.find((p: any) => p.prices?.[0]?.id === newPriceId);
    if (!newPlan?.prices?.[0]) return '';

    if (newPlan.prices[0].amount === 0) {
      return 'Activate your free plan and start using APS Recruits!';
    }
    return `You'll be charged ${formatPrice(newPlan.prices[0].amount, newPlan.prices[0].currency)} to start your subscription.`;
  };

  const handleSubscriptionUpdate = async () => {
    if (!selectedPlan || !sessionToken) {
      toast.error('Please select a plan');
      return;
    }

    const currentPlanId = getCurrentPlanId();

    // Check if this is the same plan the user already has. Promo users carry an
    // entitlement tier (e.g. regular_access) but no real paid subscription, so let
    // them "purchase" the same tier to convert their comp into a paid plan.
    if (selectedPlan === currentPlanId && !subscriptionData?.isPromotional) {
      toast.info('You are already subscribed to this plan');
      return;
    }

    try {
      setProcessing(true);

      // One plan (Premium retired 2026-09-11): every web purchase is the APS
      // Membership, i.e. the 'regular' tier.
      const planName = plans?.find(p => p.prices?.[0]?.id === selectedPlan)?.name?.toLowerCase() || '';
      const planTier = 'regular' as const;

      // Use RevenueCat redirect checkout for payment
      // First, prepare for purchase (revokes any promotional entitlements and checks cross-store)
      const prepareResult = await preparePurchase(planTier);
      if (!prepareResult.success) {
        // KGH-790: a team-covered family has nothing to buy (409 TEAM_COVERED).
        // That's information, not a failure — say so, and refresh when the
        // backend just synced the team grant to RevenueCat.
        if (prepareResult.code === 'TEAM_COVERED' || prepareResult.error === 'TEAM_COVERED') {
          const baseTeamMessage = prepareResult.message
            || "Your APS Membership is included with your team, so there's nothing to purchase.";
          // The live-store variant also says where to cancel (instructions).
          const teamMessage = prepareResult.instructions
            ? `${baseTeamMessage}\n\n${prepareResult.instructions}`
            : baseTeamMessage;
          if (prepareResult.managementUrl) {
            // Also paying through a live store: offer its manage link to stop paying.
            setPendingManagePortal({
              managementUrl: prepareResult.managementUrl,
              message: teamMessage,
              action: 'team_covered',
            });
            setManagePortalConfirmOpen(true);
          } else {
            toast.info(teamMessage, 8000);
          }
          if (prepareResult.rcSynced) {
            await Promise.all([loadSubscriptionData(), refetchSubscription()]);
          }
          return;
        }
        // Show cross-store or other blocking error with instructions
        const errorMsg = prepareResult.instructions
          ? `${prepareResult.message}\n\n${prepareResult.instructions}`
          : prepareResult.message || 'Unable to process subscription change';
        toast.error(errorMsg);
        return;
      }

      // An active web (RC Billing) subscription already IS the membership —
      // there is only one plan, so there is nothing to switch to (this also
      // covers a legacy premium web subscriber: no upgrade/downgrade detour).
      const webStores = ['rc_billing'];
      const currentSubStatus = await revenueCatAPI.getSubscriptionStatus(sessionToken!);

      if (currentSubStatus.isActive && webStores.includes(currentSubStatus.store || '')) {
        toast.info("You're already an APS member.");
        return;
      }

      // Find the matching RevenueCat package
      const selectedPackage = revenueCatPackages.find(pkg =>
        pkg.identifier === selectedPlan ||
        (pkg.identifier === REVENUECAT_CONFIG.packages.regular && selectedPlan.includes('regular'))
      );

      let packageToUse = selectedPackage;
      
      if (!packageToUse) {
        // Try to find by matching plan name/identifier
        packageToUse = revenueCatPackages.find(pkg => 
          pkg.identifier.toLowerCase().includes(planName || '') ||
          planName?.includes(pkg.identifier.toLowerCase())
        );
        
        if (!packageToUse) {
          toast.error('Unable to find the selected plan. Please try again.');
          return;
        }
      }

      // Call RevenueCat's purchasePackage - this will redirect to RC Billing checkout
      // When the user completes or cancels, they'll be redirected back
      const result = await purchasePackage(packageToUse, user?.email);
      
      // If we get here, the purchase was successful
      toast.success('Successfully subscribed to APS Membership!');

      // Refresh subscription and family data
      await Promise.all([
        loadSubscriptionData(),
        loadFamily()
      ]);
      
      // Clear selection
      setSelectedPlan(null);
      setCouponCode('');

    } catch (error: any) {
      console.error('[RevenueCat] Subscription update error:', error);
      
      // Handle various non-error scenarios gracefully
      // RevenueCat may throw these when the user returns from redirect checkout
      const errorMessage = error?.message?.toLowerCase() || '';
      
      if (
        errorMessage.includes('cancelled') || 
        errorMessage.includes('canceled') ||
        errorMessage.includes('closed') ||
        errorMessage.includes('aborted') ||
        errorMessage.includes('user cancelled')
      ) {
        // User intentionally cancelled - just show info, not error
        toast.info('Purchase cancelled');
      } else if (
        errorMessage.includes('purchase already in progress') ||
        errorMessage.includes('pending')
      ) {
        // Still processing - refresh to check status
        await loadSubscriptionData();
        toast.info('Checking purchase status...');
      } else if (!errorMessage) {
        // Empty error - likely just returning from redirect
        // Silently refresh subscription data
        await loadSubscriptionData();
      } else {
        // Actual error
        toast.error(error.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handle confirmed promo redemption when user has paid subscription (Google Play only)
  const handleConfirmPromoRedemption = async () => {
    if (!pendingPromoRedemption || !sessionToken) return;

    try {
      setProcessing(true);
      setPromoCancelConfirmModalOpen(false);

      const result = await revenueCatAPI.redeemPromoCode(
        sessionToken,
        pendingPromoRedemption.code,
        pendingPromoRedemption.planTier,
        true // cancelExistingSubscription = true
      );

      if (result.success) {
        // This path is now only for Google Play subscriptions
        toast.success(`${result.message} Your Google Play subscription has been cancelled and refunded.`);

        // Refresh subscription and family data
        await Promise.all([
          loadSubscriptionData(),
          loadFamily()
        ]);
        
        // Clear selection
        setSelectedPlan(null);
        setCouponCode('');
      } else {
        toast.error(result.message || 'Failed to redeem promo code');
      }
    } catch (error: any) {
      console.error('Error confirming promo redemption:', error);
      toast.error(error.message || 'Failed to process promo code');
    } finally {
      setProcessing(false);
      setPendingPromoRedemption(null);
    }
  };

  const loadMyTeams = async (athleteProfileId?: string) => {
    if (!sessionToken) return;
    try {
      const result = await teamCodeAPI.getMyTeams(sessionToken, athleteProfileId);
      if (result.teams && athleteProfileId) {
        setAthleteTeams(prev => ({ ...prev, [athleteProfileId]: result.teams }));
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const handleJoinTeam = async (athleteProfileId: string) => {
    const code = (athleteTeamCode[athleteProfileId] || '').trim();
    if (!code || !sessionToken) return;
    setAthleteJoiningTeam(prev => ({ ...prev, [athleteProfileId]: true }));
    setAthleteTeamCodeError(prev => ({ ...prev, [athleteProfileId]: '' }));
    try {
      const result = await teamCodeAPI.redeemCode(sessionToken, code, athleteProfileId);
      // KGH-790 round 8: a join after the code's sign-up deadline still gets a
      // roster spot but no access; the server explains that in accessMessage,
      // so show it instead of the plain success line. rcSynced=false needs no
      // UI — the DB already holds the access and /status refreshes as usual.
      if (result?.access === 'none' && result.accessMessage) {
        toast.info(result.accessMessage, 8000);
      } else {
        toast.success('Successfully joined the team!');
      }
      setAthleteTeamCode(prev => ({ ...prev, [athleteProfileId]: '' }));
      await loadMyTeams(athleteProfileId);
    } catch (error: any) {
      setAthleteTeamCodeError(prev => ({ ...prev, [athleteProfileId]: error.message || 'Invalid team code' }));
    } finally {
      setAthleteJoiningTeam(prev => ({ ...prev, [athleteProfileId]: false }));
    }
  };

  const validateFamilyName = (name: string): string | null => {
    if (!name.trim()) return 'Family name is required';
    if (name.length < 3) return 'Family name must be at least 3 characters';
    if (name.length > 25) return 'Family name must be 25 characters or less';
    if (!/^[\p{L}\s'.-]+$/u.test(name)) return 'Family name can only contain letters, spaces, hyphens, periods, and apostrophes';
    return null;
  };

  const handleUpdateFamilyName = async () => {
    const error = validateFamilyName(familyName);
    if (error) {
      setFamilyNameError(error);
      return;
    }

    setIsSaving(true);
    setFamilyNameError('');
    
    try {
      await updateFamily({ name: familyName.trim() });
      toast.success('Family name saved.');
      // Close once saved. Stay open if Your name still has unsaved typing.
      if (!isMyNameDirty) onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update family name');
      setFamilyNameError(error.message || 'Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMyName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionToken || !myGuardian || savingMyName || !isMyNameDirty) return;
    const first = myFirstName.trim().replace(/\s+/g, ' ');
    const last = myLastName.trim().replace(/\s+/g, ' ');
    const errors: { first?: string; last?: string } = {};
    if (!first) errors.first = 'Enter your first name.';
    else if (first.length > 50) errors.first = 'Use 50 characters or fewer.';
    if (last.length > 50) errors.last = 'Use 50 characters or fewer.';
    setMyNameErrors(errors);
    if (errors.first || errors.last) return;

    setMyNameSaveError(null);
    setSavingMyName(true);
    try {
      const res = await guardianAPI.updateProfile(sessionToken, myGuardian.id, { firstName: first, lastName: last });
      setMyFirstName(res.profile.firstName);
      setMyLastName(res.profile.lastName);
      // The header reads the name from the session.
      if (user && res.profile.userName && res.profile.userName !== user.name) {
        setUser({ ...user, name: res.profile.userName });
      }
      await loadDetailedFamily();
      toast.success('Name saved.');
      // Close once saved. Stay open if the family name still has unsaved typing.
      if (!isFamilyNameDirty) onClose();
    } catch (error: any) {
      const status = error?.status;
      setMyNameSaveError((status === 400 || status === 403) && error?.message
        ? error.message
        : 'Your name did not save. Try again.');
    } finally {
      setSavingMyName(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingInvite(true);

    try {
      // No role param — picker was theater; backend now defaults the
      // invitee's role to whatever they pick on join.
      await inviteMember(inviteEmail.trim());
      toast.success(`Invitation sent to ${inviteEmail}!`);
      setInviteEmail('');
      await loadPendingInvitations();
    } catch (error: any) {
      // Free-tier gate: route through the standard paywall instead of a
      // toast error so it matches every other freemium upsell.
      if (error?.code === 'FREE_TIER_INVITE_LIMIT' || error?.details?.code === 'FREE_TIER_INVITE_LIMIT') {
        requirePaid('Family Invites', () => { /* no-op — paywall handles routing */ });
      } else {
        toast.error(error.message || 'Failed to send invitation');
      }
    } finally {
      setSendingInvite(false);
    }
  };

  // Resend / cancel for the pending list. Both refresh the list after.
  const handleResendInvite = async (invitationId: string, email: string) => {
    setBusyInviteId(invitationId);
    try {
      await resendInvitation(invitationId);
      toast.success(`Invitation re-sent to ${email}`);
      await loadPendingInvitations();
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend invitation');
    } finally {
      setBusyInviteId(null);
    }
  };

  // Opens the branded ConfirmDialog. The actual cancel-invitation API call
  // runs in confirmCancelInvite below, fired from the dialog's onConfirm.
  const handleCancelInvite = (invitationId: string, email: string) => {
    setPendingCancelInvite({ id: invitationId, email });
  };

  const confirmCancelInvite = async () => {
    if (!pendingCancelInvite) return;
    const { id, email } = pendingCancelInvite;
    setBusyInviteId(id);
    try {
      await cancelInvitation(id);
      toast.success(`Invitation to ${email} cancelled`);
      await loadPendingInvitations();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel invitation');
    } finally {
      setBusyInviteId(null);
      setPendingCancelInvite(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!sessionToken) {
      toast.error('Authentication required');
      return;
    }

    setIsDeleting(true);

    try {
      await accountAPI.deleteAccount(sessionToken);
      
      toast.success('Your account has been scheduled for deletion. You have 30 days to reactivate.');
      
      // Close modals
      setDeleteConfirmModalOpen(false);
      onClose();
      
      // Logout after a brief delay
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (error: any) {
      console.error('Account deletion error:', error);
      toast.error(error.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  // When a nested dialog/overlay is open, don't let a backdrop click or Escape
  // tear down the whole Account Settings modal underneath it — the nested
  // surface handles its own dismissal.
  const anyOverlayOpen =
    contactSupportModalOpen ||
    notificationPreferencesModalOpen ||
    emailTemplatesModalOpen ||
    xTagListsModalOpen ||
    memberDetailsModalOpen ||
    deleteConfirmModalOpen ||
    promoCancelConfirmModalOpen ||
    managePortalConfirmOpen ||
    showPaymentForm ||
    showRemoveConfirm ||
    !!pendingCancelInvite ||
    !!pendingRemoveMember ||
    processing;

  return (
    <>
      {/* Main Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        ariaLabel="Account Settings"
        isDirty={isFamilyNameDirty || isMyNameDirty}
        disableClose={anyOverlayOpen}
        overlayStyle={{ padding: isMobile ? '0' : '20px' }}
        contentStyle={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '900px',
          maxHeight: isMobile ? '100%' : '90vh',
          height: isMobile ? '100%' : 'auto',
          borderRadius: isMobile ? '0' : '16px',
          overflow: 'hidden',
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header */}
          <div
            style={{
              padding: isMobile ? '16px 20px' : '24px 32px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '600',
                color: '#1F2937',
                margin: 0,
              }}
            >
              Account Settings
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#6B7280',
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(249, 85, 13, 0.2)',
                  borderTop: '4px solid #f9550d',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ marginTop: '16px', fontSize: '16px', color: '#6B7280' }}>
                Loading account settings...
              </p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '32px',
              }}
            >
              {/* Your Name Section. Guardians only. */}
              {myGuardian && (
                <form onSubmit={handleSaveMyName} noValidate style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <User size={24} color="#f9550d" />
                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                      Your Name
                    </h3>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    <Field id="account-my-first-name" label="First name" required error={myNameErrors.first}>
                      <TextInput
                        {...fieldAria('account-my-first-name', { error: myNameErrors.first })}
                        type="text"
                        value={myFirstName}
                        maxLength={50}
                        invalid={!!myNameErrors.first}
                        autoComplete="given-name"
                        disabled={savingMyName}
                        onChange={(e) => {
                          setMyFirstName(e.target.value);
                          if (myNameErrors.first) setMyNameErrors((p) => ({ ...p, first: undefined }));
                          if (myNameSaveError) setMyNameSaveError(null);
                        }}
                      />
                    </Field>
                    <Field id="account-my-last-name" label="Last name" error={myNameErrors.last}>
                      <TextInput
                        {...fieldAria('account-my-last-name', { error: myNameErrors.last })}
                        type="text"
                        value={myLastName}
                        maxLength={50}
                        invalid={!!myNameErrors.last}
                        autoComplete="family-name"
                        disabled={savingMyName}
                        onChange={(e) => {
                          setMyLastName(e.target.value);
                          if (myNameErrors.last) setMyNameErrors((p) => ({ ...p, last: undefined }));
                          if (myNameSaveError) setMyNameSaveError(null);
                        }}
                      />
                    </Field>
                  </div>
                  {myNameSaveError && (
                    <p role="alert" style={{ fontSize: '14px', color: '#dc2626', margin: '0 0 12px 0' }}>
                      {myNameSaveError}
                    </p>
                  )}
                  <Button type="submit" size="lg" fullWidth loading={savingMyName} disabled={!isMyNameDirty || savingMyName}>
                    Save name
                  </Button>
                </form>
              )}

              {/* Contact Support Section */}
              <div style={{ marginBottom: '32px' }}>
                <button
                  onClick={() => setContactSupportModalOpen(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <HelpCircle size={24} color="#f9550d" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Contact Support
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Get help or report an issue
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} color="#6B7280" />
                </button>

                <button
                  onClick={() => requirePaid('Email Templates', () => setEmailTemplatesModalOpen(true))}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Mail size={24} color="#f9550d" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Email Templates
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Create and manage reusable email templates
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} color="#6B7280" />
                </button>

                <button
                  onClick={() => setXTagListsModalOpen(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Tag size={24} color="#f9550d" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Tag Lists
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Save handles you tag on X — team, teammates, socials
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} color="#6B7280" />
                </button>

                <button
                  onClick={() => setNotificationPreferencesModalOpen(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Bell size={24} color="#f9550d" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Notification Preferences
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Manage how you receive notifications
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} color="#6B7280" />
                </button>

                <button
                  onClick={() => setDeleteConfirmModalOpen(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AlertCircle size={24} color="#DC2626" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Delete Account
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Permanently remove your account
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} color="#6B7280" />
                </button>
              </div>

              {/* Connections Section — Gmail / X connect + reconnect, folded in
                  from the standalone /settings/connections page so Settings is
                  one surface. */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <LinkIcon size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Connections
                  </h3>
                </div>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>
                  Connect Gmail and X so we can track coach outreach. Disconnect anytime from the same row.
                </p>
                <SocialConnectPanel
                  athleteId={connectionsAthleteId}
                  hideYouTube={user?.user_type === 'staff'}
                  title="Your accounts"
                />
              </div>

              {/* Family Settings Section */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Users size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Family Settings
                  </h3>
                </div>

                {!isBillingOwner && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      marginBottom: '16px',
                    }}
                  >
                    <AlertCircle size={16} color="#6B7280" />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Only the billing owner can modify family settings
                    </span>
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  {/* The onFocus/onBlur border swap went with the inline
                      `outline: none` it sat beside: the swap only painted a
                      border, and the outline it suppressed was the global
                      :focus-visible ring, so a keyboard user saw nothing. The
                      non-owner's grey fill is `.aps-input:disabled` now, which
                      is what the field already was — the hand-painted fill and
                      muted ink said the same thing twice. */}
                  <label
                    htmlFor="account-family-name"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px',
                    }}
                  >
                    Family Name
                  </label>
                  <TextInput
                    id="account-family-name"
                    type="text"
                    value={familyName}
                    onChange={(e) => {
                      setFamilyName(e.target.value);
                      setFamilyNameError('');
                    }}
                    disabled={!isBillingOwner}
                    invalid={!!familyNameError}
                    aria-describedby={familyNameError ? 'account-family-name-error' : undefined}
                    placeholder="Enter family name"
                    maxLength={25}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '15px',
                      transition: 'all 0.2s ease',
                      cursor: isBillingOwner ? 'text' : 'not-allowed',
                    }}
                  />
                  {familyNameError && (
                    <p id="account-family-name-error" role="alert" style={{ fontSize: '14px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>
                      {familyNameError}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleUpdateFamilyName}
                  disabled={!isBillingOwner || isSaving || familyName === family?.name}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: 'white',
                    background: (!isBillingOwner || isSaving || familyName === family?.name) ? '#9CA3AF' : 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (!isBillingOwner || isSaving || familyName === family?.name) ? 'not-allowed' : 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxShadow: (!isBillingOwner || isSaving || familyName === family?.name) ? 'none' : '0 4px 12px rgba(249, 85, 13, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (isBillingOwner && !isSaving && familyName !== family?.name) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 85, 13, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    if (isBillingOwner && !isSaving && familyName !== family?.name) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
                    }
                  }}
                >
                  {isSaving ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                      Updating...
                    </div>
                  ) : (
                    'Update Family Name'
                  )}
                </button>
              </div>

              {/* Family Members Section */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Users size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Family Members
                  </h3>
                </div>

                {detailedFamily ? (
                  <div>
                    {/* Summary Card */}
                    <div
                      style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                      }}
                    >
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        {(() => {
                          const uniqueEmails = new Set<string>();
                          detailedFamily.athletes?.forEach((athlete: any) => {
                            athlete.linked_users?.forEach((linkedUser: any) => {
                              uniqueEmails.add(linkedUser.email);
                            });
                          });
                          detailedFamily.guardians?.forEach((guardian: any) => {
                            guardian.linked_users?.forEach((linkedUser: any) => {
                              uniqueEmails.add(linkedUser.email);
                            });
                          });
                          detailedFamily.unlinked_users?.forEach((user: any) => {
                            uniqueEmails.add(user.email);
                          });
                          const count = uniqueEmails.size;
                          return `${count} Family Member${count !== 1 ? 's' : ''}`;
                        })()}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        {detailedFamily.summary?.total_athletes || 0} Athletes • {detailedFamily.summary?.total_guardians || 0} Guardians
                      </div>
                    </div>

                    {/* Athletes */}
                    {detailedFamily.athletes && detailedFamily.athletes.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: '0 0 12px 0' }}>
                          Athletes ({detailedFamily.athletes.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {detailedFamily.athletes.map((athlete: any) => (
                            <div
                              key={athlete.id}
                              onClick={() => {
                                setSelectedMember({ ...athlete, type: 'athlete' });
                                setMemberDetailsModalOpen(true);
                              }}
                              style={{
                                background: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                padding: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#D1D5DB';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                                    {athlete.first_name} {athlete.last_name}
                                  </span>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '2px 8px',
                                      background: '#ECFDF5',
                                      color: '#059669',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    <Zap size={12} />
                                    Athlete
                                  </span>
                                </div>

                                {athlete.email && (
                                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                                    {athlete.email}
                                  </div>
                                )}

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                  {athlete.sport && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280' }}>
                                      <Activity size={14} />
                                      {athlete.sport}
                                    </div>
                                  )}
                                  {athlete.graduation_year && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280' }}>
                                      <Calendar size={14} />
                                      Class of {athlete.graduation_year}
                                    </div>
                                  )}
                                  {athlete.position && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280' }}>
                                      <Target size={14} />
                                      {athlete.position}
                                    </div>
                                  )}
                                </div>

                                {athlete.linked_users && athlete.linked_users.length > 0 && (
                                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>
                                      Connected Accounts:
                                    </div>
                                    {athlete.linked_users.map((linkedUser: any, idx: number) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                                        <User size={12} />
                                        {linkedUser.email}
                                        {linkedUser.is_billing_owner && (
                                          <span
                                            style={{
                                              padding: '2px 6px',
                                              background: '#FFF7ED',
                                              color: '#F97316',
                                              borderRadius: '4px',
                                              fontSize: '11px',
                                              fontWeight: '600',
                                              marginLeft: '4px',
                                            }}
                                          >
                                            Billing Owner
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Team Code — per athlete */}
                                <div
                                  style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Users size={14} color="#f9550d" />
                                    Team Code
                                  </div>

                                  {/* Existing team memberships for this athlete */}
                                  {(athleteTeams[athlete.id] || []).length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                                      {(athleteTeams[athlete.id] || []).map((team: any) => (
                                        <div
                                          key={team.id}
                                          style={{
                                            padding: '8px 12px',
                                            background: 'rgba(22, 163, 74, 0.05)',
                                            border: '1px solid rgba(22, 163, 74, 0.3)',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                          }}
                                        >
                                          <div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              <Check size={13} color="#16a34a" aria-label="Joined" />
                                              {team.team_name}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '1px' }}>
                                              {team.org_name}{team.sport ? ` · ${team.sport}` : ''}
                                            </div>
                                          </div>
                                          <code style={{
                                            fontSize: '11px',
                                            letterSpacing: '1px',
                                            color: '#374151',
                                            background: 'rgba(0,0,0,0.05)',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontFamily: 'monospace',
                                          }}>
                                            {team.team_code || team.joined_via_code}
                                          </code>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Team code input */}
                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                                    <TextInput
                                      id={`account-team-code-${athlete.id}`}
                                      aria-label="Team or promo code"
                                      type="text"
                                      value={athleteTeamCode[athlete.id] || ''}
                                      onChange={(e) => {
                                        setAthleteTeamCode(prev => ({ ...prev, [athlete.id]: e.target.value.toUpperCase() }));
                                        setAthleteTeamCodeError(prev => ({ ...prev, [athlete.id]: '' }));
                                      }}
                                      placeholder="e.g. TEAM2024"
                                      invalid={!!athleteTeamCodeError[athlete.id]}
                                      style={{
                                        /* width:auto so flex, not the primitive's
                                         * 100%, sizes it next to the Join button. */
                                        width: 'auto',
                                        flex: 1,
                                        padding: '8px 10px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (athleteTeamCode[athlete.id] || '').trim() && !athleteJoiningTeam[athlete.id]) {
                                          handleJoinTeam(athlete.id);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => handleJoinTeam(athlete.id)}
                                      disabled={!(athleteTeamCode[athlete.id] || '').trim() || athleteJoiningTeam[athlete.id]}
                                      style={{
                                        padding: '8px 12px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        background: !(athleteTeamCode[athlete.id] || '').trim() ? 'rgba(249,85,13,0.4)' : '#f9550d',
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: !(athleteTeamCode[athlete.id] || '').trim() ? 'default' : 'pointer',
                                        fontFamily: 'Manrope, sans-serif',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {athleteJoiningTeam[athlete.id] ? 'Joining...' : 'Join'}
                                    </button>
                                  </div>

                                  {athleteTeamCodeError[athlete.id] && (
                                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#f87171' }}>
                                      {athleteTeamCodeError[athlete.id]}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <ChevronRight size={20} color="#6B7280" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Guardians */}
                    {detailedFamily.guardians && detailedFamily.guardians.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: '0 0 12px 0' }}>
                          Guardians ({detailedFamily.guardians.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {detailedFamily.guardians.map((guardian: any) => (
                            <div
                              key={guardian.id}
                              onClick={() => {
                                setSelectedMember({ ...guardian, type: 'guardian' });
                                setMemberDetailsModalOpen(true);
                              }}
                              style={{
                                background: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                padding: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#D1D5DB';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                                    {guardian.first_name} {guardian.last_name}
                                  </span>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '2px 8px',
                                      background: '#EFF6FF',
                                      color: '#3B82F6',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    <Shield size={12} />
                                    Guardian
                                  </span>
                                </div>

                                {guardian.email && (
                                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                                    {guardian.email}
                                  </div>
                                )}

                                {guardian.relationship_to_athlete && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>
                                    <Heart size={14} />
                                    {guardian.relationship_to_athlete}
                                  </div>
                                )}

                                {guardian.linked_users && guardian.linked_users.length > 0 && (
                                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>
                                      Connected Accounts:
                                    </div>
                                    {guardian.linked_users.map((linkedUser: any, idx: number) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                                        <User size={12} />
                                        {linkedUser.email}
                                        {linkedUser.is_billing_owner && (
                                          <span
                                            style={{
                                              padding: '2px 6px',
                                              background: '#FFF7ED',
                                              color: '#F97316',
                                              borderRadius: '4px',
                                              fontSize: '11px',
                                              fontWeight: '600',
                                              marginLeft: '4px',
                                            }}
                                          >
                                            Billing Owner
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <ChevronRight size={20} color="#6B7280" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Unlinked Users */}
                    {detailedFamily.unlinked_users && detailedFamily.unlinked_users.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: '0 0 4px 0' }}>
                          Unlinked Accounts ({detailedFamily.unlinked_users.length})
                        </h4>
                        <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 12px 0' }}>
                          These accounts need to create their profiles
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {detailedFamily.unlinked_users.map((user: any) => (
                            <div
                              key={user.id}
                              style={{
                                background: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '12px',
                                padding: '16px',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                                  {user.name || user.email}
                                </span>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 8px',
                                    background: '#FFF7ED',
                                    color: '#B5390A',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                  }}
                                >
                                  <AlertCircle size={12} />
                                  Needs Profile
                                </span>
                              </div>

                              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                                {user.email}
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280' }}>
                                  <User size={14} />
                                  {user.user_type || 'Unknown'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6B7280' }}>
                                  <Calendar size={14} />
                                  Joined {new Date(user.joined_at).toLocaleDateString()}
                                </div>
                              </div>

                              {user.is_billing_owner && (
                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
                                  <span
                                    style={{
                                      padding: '4px 8px',
                                      background: '#FFF7ED',
                                      color: '#F97316',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                    }}
                                  >
                                    Billing Owner
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {(!detailedFamily.athletes || detailedFamily.athletes.length === 0) &&
                      (!detailedFamily.guardians || detailedFamily.guardians.length === 0) &&
                      (!detailedFamily.unlinked_users || detailedFamily.unlinked_users.length === 0) && (
                        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                          <Users size={48} color="#9CA3AF" style={{ margin: '0 auto 16px' }} />
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                            No Family Members
                          </div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>
                            Invite family members to get started
                          </div>
                        </div>
                      )}
                  </div>
                ) : familyMembersError ? (
                  <ErrorState
                    compact
                    message="We couldn't load your family members."
                    onRetry={() => { void loadDetailedFamily(); }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px' }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid rgba(249, 85, 13, 0.2)',
                        borderTop: '3px solid #f9550d',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 12px',
                      }}
                    />
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      Loading family members...
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Settings Section */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <CreditCard size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Subscription Settings
                  </h3>
                </div>

                {!isBillingOwner && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#FEF3C7',
                      border: '1px solid #FCD34D',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <AlertCircle size={16} color="#92400E" />
                    <span style={{ fontSize: '14px', color: '#92400E' }}>
                      Only the billing owner can modify subscription settings
                    </span>
                  </div>
                )}

                {/* Consolidated Subscription Card */}
                {subscriptionData?.status === 'active' && (subscriptionData.store === 'app_store' || subscriptionData.store === 'play_store' || subscriptionData.store === 'rc_billing' || subscriptionData.store === 'stripe' || subscriptionData.store === 'promotional') && (
                  <div
                    style={{
                      background: '#F0FDF4',
                      border: '1px solid #22C55E',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    {/* Status Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        background: '#22C55E', 
                        padding: '4px 10px', 
                        borderRadius: '12px' 
                      }}>
                        <CheckCircle size={14} color="#FFFFFF" />
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF' }}>Active</span>
                      </div>
                    </div>

                    {/* Plan Name & Price */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#151133', marginBottom: '4px' }}>
                        {/* One plan (Premium retired 2026-09-11): any active tier — a
                            legacy premium_access grant included — is the APS Membership. */}
                        {plans?.[0]?.name || 'APS Membership'}
                      </div>
                      {/* Price display with discount support */}
                      {(() => {
                        // TODO(audit #194): drive price from backend plan/RC package.
                        // Derives the base price from the Stripe plan already fetched
                        // into `plans` for this tier — never a fabricated literal.
                        const promo = subscriptionData.promoDetails;

                        // FREEPLAN takes priority - check both isPromotional and promoDetails.discountType === 'free'
                        if (subscriptionData.isPromotional || promo?.discountType === 'free') {
                          return <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>FREE</div>;
                        }

                        // The single APS Membership plan is the 'regular' tier. A legacy
                        // premium grant matches no plan, so it gets no price block at
                        // all — never quote a made-up price to a grandfathered payer.
                        const matchingPlan = plans?.find((plan: any) =>
                          (plan.metadata?.tier || 'regular') === subscriptionData.tier
                        );
                        const priceObj: any = matchingPlan?.prices?.[0];

                        // Real price unavailable: render nothing rather than a wrong
                        // number or a bare orange placeholder.
                        if (!priceObj || typeof priceObj.amount !== 'number') {
                          return null;
                        }

                        const currency = priceObj.currency || 'usd';
                        const intervalLabel = priceObj.interval === 'year' ? '/yr' : '/mo';
                        const baseCents = priceObj.amount;
                        const hasDiscount = promo && promo.discountType && promo.discountType !== 'free';

                        if (hasDiscount) {
                          let discountedCents = baseCents;
                          if (promo.discountType === 'percent_off' && promo.discountValue) {
                            discountedCents = Math.round(baseCents * (1 - promo.discountValue / 100));
                          } else if (promo.discountType === 'amount_off' && promo.discountValue) {
                            // discountValue is in minor units (cents), same basis as baseCents.
                            discountedCents = Math.max(0, baseCents - promo.discountValue);
                          }
                          return (
                            <div>
                              <span style={{ fontSize: '16px', color: '#9CA3AF', textDecoration: 'line-through', marginRight: '8px' }}>
                                {formatPrice(baseCents, currency)}{intervalLabel}
                              </span>
                              <span style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                                {formatPrice(discountedCents, currency)}{intervalLabel}
                              </span>
                            </div>
                          );
                        }

                        return <div style={{ fontSize: '24px', fontWeight: '700', color: '#F9550D' }}>{formatPrice(baseCents, currency)}{intervalLabel}</div>;
                      })()}
                      {/* Show promo code if active - but not for FREEPLAN (they see FREE price already) */}
                      {subscriptionData.promoDetails?.code && subscriptionData.promoDetails?.discountType !== 'free' && (
                        <div style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          marginTop: '6px',
                          background: '#ECFDF5', 
                          padding: '4px 8px', 
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#059669'
                        }}>
                          <Tag size={12} />
                          <span>{subscriptionData.promoDetails.code}</span>
                          {subscriptionData.promoDetails.discountType === 'percent_off' && subscriptionData.promoDetails.discountValue && (
                            <span>({subscriptionData.promoDetails.discountValue}% off)</span>
                          )}
                          {subscriptionData.promoDetails.discountType === 'amount_off' && subscriptionData.promoDetails.discountValue && (
                            <span>(${(subscriptionData.promoDetails.discountValue / 100).toFixed(0)} off)</span>
                          )}
                          {subscriptionData.promoDetails.isLifetime && <span>(Lifetime)</span>}
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {/* KGH-790: a lifetime promo (team seat, FREEPLAN) carries
                          RevenueCat's ~200-years-out date; it has no end date to
                          show. A promotional grant never renews. */}
                      {subscriptionData.expiresAt && new Date(subscriptionData.expiresAt).getUTCFullYear() < 2100 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Calendar size={14} color="#6B7280" />
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {subscriptionData.willRenew && !subscriptionData.isPromotional ? 'Renews' : 'Expires'} {new Date(subscriptionData.expiresAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {/* Only show store for paid subscriptions (not FREEPLAN) */}
                      {!subscriptionData.isPromotional && subscriptionData.promoDetails?.discountType !== 'free' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {subscriptionData.store === 'app_store' || subscriptionData.store === 'play_store' ? (
                            <Smartphone size={14} color="#6B7280" />
                          ) : (
                            <Globe size={14} color="#6B7280" />
                          )}
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {subscriptionData.store === 'app_store' ? 'via App Store' : 
                             subscriptionData.store === 'play_store' ? 'via Google Play' :
                             'via Web'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Manage Button - only for PAID subscriptions and billing owners (NOT FREEPLAN) */}
                    {isBillingOwner && !subscriptionData.isPromotional && subscriptionData.promoDetails?.discountType !== 'free' && (
                      <a
                        href={subscriptionData.store === 'app_store' 
                          ? 'https://apps.apple.com/account/subscriptions'
                          : subscriptionData.store === 'play_store'
                          ? 'https://play.google.com/store/account/subscriptions'
                          : manageSubscriptionUrl(subscriptionData, user?.email)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          marginTop: '12px',
                          padding: '10px 16px',
                          background: '#DBEAFE',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#3B82F6',
                          textDecoration: 'none',
                        }}
                      >
                        {subscriptionData.store === 'app_store' 
                          ? 'Manage in App Store'
                          : subscriptionData.store === 'play_store'
                          ? 'Manage in Google Play'
                          : 'Manage Subscription'}
                        <ExternalLink size={14} />
                      </a>
                    )}

                    {/* Promo/comp: prompt to convert to a paid plan (grid below);
                        keep the support email for changes/cancellations. */}
                    {(subscriptionData.isPromotional || subscriptionData.promoDetails?.discountType === 'free') && (
                      <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px', textAlign: 'center', margin: '12px 0 0 0' }}>
                        {/* KGH-790: lifetime before the date — RevenueCat reports a
                            lifetime promo with a ~200-years-out expiry. */}
                        {teamCovered
                          ? "Included with your team. Your APS Membership stays active while you're on the team."
                          : lifetimeComp || (subscriptionData.expiresAt && new Date(subscriptionData.expiresAt).getUTCFullYear() >= 2100)
                            ? "Your complimentary access doesn't expire."
                            : subscriptionData.expiresAt
                              ? `Your promo access ends ${new Date(subscriptionData.expiresAt).toLocaleDateString()}. Subscribe below to keep full access.`
                              : 'Subscribe below to move to a paid plan and keep full access.'}
                        {' '}To make changes, contact support@apsrecruits.com
                      </p>
                    )}
                  </div>
                )}

                {/* Show plan selection for users with NO active subscription, AND for
                    promo/comp users — a promo isn't a real paid subscription, so surface
                    the plan grid so they can convert to a paid plan before it expires.
                    Real paid subscribers keep the manage-only card (no grid). */}
                {planGridShows && (
                  <>
                {/* A promo family already has the membership (for now), so the ask
                    is to keep it; everyone else is joining. */}
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: '0 0 4px 0' }}>
                  {subscriptionData?.isPromotional
                    ? (lifetimeComp ? 'APS Membership' : 'Keep your APS Membership')
                    : 'Become an APS Member'}
                </h4>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
                  {subscriptionData?.isPromotional
                    ? (lifetimeComp
                      ? "Your complimentary access doesn't expire. You can switch to a paid membership any time."
                      : 'Subscribe to keep full access when your promo ends.')
                    : 'One membership with full access for your whole family.'}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', opacity: isBillingOwner ? 1 : 0.6, pointerEvents: isBillingOwner ? 'auto' : 'none' }}>
                  {plans && plans.map((plan: any) => {
                    const price = plan.prices?.[0];
                    if (!price) return null;

                    const isSelected = selectedPlan === price.id;
                    const isFree = price.amount === 0;
                    const currentPlanId = getCurrentPlanId();
                    const isCurrent = currentPlanId === price.id;

                    return (
                      <div
                        key={price.id}
                        onClick={() => {
                          if (isBillingOwner && !processing) {
                            setSelectedPlan(price.id);
                          }
                        }}
                        style={{
                          background: 'white',
                          border: `2px solid ${isSelected ? '#f9550d' : '#E5E7EB'}`,
                          borderRadius: '12px',
                          padding: '20px',
                          cursor: isBillingOwner && !processing ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected ? '0 4px 12px rgba(249, 85, 13, 0.15)' : 'none',
                        }}
                        onMouseEnter={(e) => {
                          if (isBillingOwner && !processing && !isSelected) {
                            e.currentTarget.style.borderColor = '#D1D5DB';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#E5E7EB';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {/* Plan Header */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ fontSize: '20px', fontWeight: '700', color: isSelected ? '#f9550d' : '#1F2937' }}>
                                {plan.name}
                              </span>
                              {isCurrent && (
                                <span
                                  style={{
                                    padding: '4px 10px',
                                    background: '#ECFDF5',
                                    color: '#059669',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  CURRENT
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Pricing Display */}
                          <div style={{ marginBottom: '8px' }}>
                            {(() => {
                              // Show discounted pricing for current plan if it has a discount
                              if (isCurrent && subscriptionData?.discount && subscriptionData.discount.final_amount !== undefined) {
                                if (subscriptionData.discount.final_amount === 0) {
                                  // 100% free
                                  return (
                                    <div>
                                      <div style={{ fontSize: '16px', color: '#9CA3AF', textDecoration: 'line-through', marginBottom: '4px' }}>
                                        {formatPrice(price.amount, price.currency)}/{price.interval}
                                      </div>
                                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                                        Free
                                      </div>
                                      <div
                                        style={{
                                          display: 'inline-block',
                                          padding: '4px 8px',
                                          background: '#ECFDF5',
                                          color: '#059669',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                        }}
                                      >
                                        {subscriptionData.discount.coupon.name || '100% OFF'}
                                      </div>
                                    </div>
                                  );
                                } else {
                                  // Partial discount
                                  return (
                                    <div>
                                      <div style={{ fontSize: '16px', color: '#9CA3AF', textDecoration: 'line-through', marginBottom: '4px' }}>
                                        {formatPrice(price.amount, price.currency)}/{price.interval}
                                      </div>
                                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#f9550d', marginBottom: '4px' }}>
                                        {formatPrice(subscriptionData.discount.final_amount, price.currency)}
                                        <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: '500' }}>/{price.interval}</span>
                                      </div>
                                      <div
                                        style={{
                                          display: 'inline-block',
                                          padding: '4px 8px',
                                          background: '#ECFDF5',
                                          color: '#059669',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                        }}
                                      >
                                        {subscriptionData.discount.coupon.percent_off
                                          ? `${subscriptionData.discount.coupon.percent_off}% OFF`
                                          : `Save ${formatPrice(subscriptionData.discount.discount_amount, price.currency)}`
                                        }
                                      </div>
                                    </div>
                                  );
                                }
                              }

                              // Show FREE for promotional/promo code subscriptions on the current plan
                              if (isCurrent && subscriptionData?.isPromotional) {
                                const promo = subscriptionData.promoDetails;
                                const promoLabel = promo?.code || 'Promo Code';
                                const promoDescription = promo?.isLifetime 
                                  ? 'Lifetime Access' 
                                  : promo?.expiresAt 
                                    ? `Expires ${new Date(promo.expiresAt).toLocaleDateString()}`
                                    : 'Promotional Access';
                                
                                return (
                                  <div>
                                    <div style={{ fontSize: '16px', color: '#9CA3AF', textDecoration: 'line-through', marginBottom: '4px' }}>
                                      {formatPrice(price.amount, price.currency)}/{price.interval}
                                    </div>
                                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                                      Free
                                    </div>
                                    <div
                                      style={{
                                        display: 'inline-block',
                                        padding: '4px 8px',
                                        background: '#ECFDF5',
                                        color: '#059669',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        marginBottom: '4px',
                                      }}
                                    >
                                      {promoLabel}
                                    </div>
                                    {promo && (
                                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                        {promoDescription}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // Default pricing display
                              return (
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                  <span style={{ fontSize: '32px', fontWeight: '700', color: isSelected ? '#f9550d' : '#1F2937' }}>
                                    {isFree ? 'Free' : formatPrice(price.amount, price.currency)}
                                  </span>
                                  {!isFree && price.interval && (
                                    <span style={{ fontSize: '16px', color: isSelected ? '#f9550d' : '#6B7280', fontWeight: '500' }}>
                                      /{price.interval}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Plan Description */}
                        {plan.description && (
                          <p style={{ fontSize: '14px', color: isSelected ? '#374151' : '#6B7280', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                            {plan.description}
                          </p>
                        )}

                        {/* Current plan discount info */}
                        {isCurrent && getCurrentDiscount() && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 12px',
                              background: '#F0F9F0',
                              border: '1px solid #22C55E',
                              borderRadius: '8px',
                              marginBottom: '12px',
                            }}
                          >
                            <Tag size={14} color="#22C55E" />
                            <span style={{ fontSize: '13px', color: '#16A34A', fontWeight: '600' }}>
                              {getCurrentDiscount()?.name || 'Coupon Applied'}
                            </span>
                          </div>
                        )}

                        {/* Plan Features */}
                        {(() => {
                          try {
                            // Check for features in different locations (metadata or direct)
                            let featuresArray: string[] = [];
                            
                            // Direct features array (from static plans)
                            if (Array.isArray(plan.features)) {
                              featuresArray = plan.features;
                            }
                            // Features in metadata as JSON string (legacy format)
                            else if (plan.metadata?.features) {
                              const featuresString = plan.metadata.features;
                              // Convert single quotes to double quotes for valid JSON
                              const validJsonString = featuresString.replace(/'/g, '"');
                              const parsed = JSON.parse(validJsonString);
                              if (Array.isArray(parsed)) {
                                featuresArray = parsed;
                              }
                            }
                            
                            if (featuresArray.length > 0) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                                  {featuresArray.map((feature: string, index: number) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: '18px',
                                          height: '18px',
                                          background: isSelected ? '#f9550d' : '#10B981',
                                          borderRadius: '50%',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Check size={12} color="white" />
                                      </div>
                                      <span style={{ fontSize: '14px', color: '#374151' }}>{feature}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                          } catch (error) {
                            // Silently fail if features can't be parsed
                          }
                          return null;
                        })()}
                      </div>
                    );
                  })}
                </div>

                {/* Promo or Team Code Section — partner-issued team codes
                    redeem on the same surface but are a distinct concept
                    to users. Always surface both labels. */}
                {isBillingOwner && !(subscriptionData?.status === 'active') && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', margin: '0 0 8px 0' }}>
                      Have a Promo or Team Code?
                    </h4>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <TextInput
                        id="account-promo-code"
                        aria-label="Promo or team code"
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && couponCode.trim() && !couponLoading) {
                            handleApplyPromoCode();
                          }
                        }}
                        placeholder="Enter promo or team code"
                        disabled={couponLoading}
                        invalid={!!couponError}
                        style={{
                          /* width:auto so flex sizes it beside Apply. */
                          width: 'auto',
                          flex: 1,
                          padding: '12px 16px',
                          border: couponError ? '2px solid #EF4444' : '1px solid #E5E7EB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          transition: 'border-color 0.2s ease',
                        }}
                      />
                      <button
                        onClick={handleApplyPromoCode}
                        disabled={couponLoading || !couponCode.trim()}
                        style={{
                          padding: '12px 24px',
                          background: (couponLoading || !couponCode.trim()) ? '#D1D5DB' : '#f9550d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (couponLoading || !couponCode.trim()) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '80px',
                        }}
                        onMouseEnter={(e) => {
                          if (!couponLoading && couponCode.trim()) {
                            e.currentTarget.style.background = '#e04a0a';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!couponLoading && couponCode.trim()) {
                            e.currentTarget.style.background = '#f9550d';
                          }
                        }}
                      >
                        {couponLoading ? (
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                        ) : (
                          'Apply'
                        )}
                      </button>
                    </div>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 0 0' }}>
                      Enter a promo or team code to get free access
                    </p>

                    {couponError && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginTop: '8px',
                          padding: '8px 12px',
                          background: '#FEF2F2',
                          border: '1px solid #FCA5A5',
                          borderRadius: '6px',
                        }}
                      >
                        <AlertCircle size={14} color="#EF4444" />
                        <span style={{ fontSize: '13px', color: '#EF4444' }}>
                          {couponError}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Update Subscription Button. Promo users may select their current
                    tier to convert the comp into a real paid subscription. */}
                {selectedPlan && (selectedPlan !== getCurrentPlanId() || subscriptionData?.isPromotional) && (
                  <div style={{ marginTop: '24px' }}>
                    <button
                      onClick={handleSubscriptionUpdate}
                      disabled={processing || !isBillingOwner}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: (processing || !isBillingOwner) ? '#D1D5DB' : '#f9550d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: (processing || !isBillingOwner) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                      onMouseEnter={(e) => {
                        if (!processing && isBillingOwner) {
                          e.currentTarget.style.background = '#e04a0a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!processing && isBillingOwner) {
                          e.currentTarget.style.background = '#f9550d';
                        }
                      }}
                    >
                      {processing ? (
                        <>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              border: '3px solid rgba(255, 255, 255, 0.3)',
                              borderTop: '3px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                          Processing...
                        </>
                      ) : (
                        (() => {
                          const plan = plans?.find((p: any) => p.prices?.[0]?.id === selectedPlan);
                          const amount = plan?.prices?.[0]?.amount || 0;
                          const price = amount > 0 ? `$${(amount / 100).toFixed(2)}` : '';

                          // One plan: the grid only starts the membership (a promo
                          // user converting their comp included) — no upgrade /
                          // downgrade / change-plan labels.
                          return price ? `Subscribe - ${price}` : 'Subscribe Now';
                        })()
                      )}
                    </button>

                    {/* Change description */}
                    {!processing && (
                      <p style={{ fontSize: '13px', color: '#6B7280', margin: '8px 0 0 0', textAlign: 'center' }}>
                        {getChangeDescription(selectedPlan)}
                      </p>
                    )}
                  </div>
                )}
                  </>
                )}
              </div>

              {/* Referral Code Section — only shown if user has a referral code (manual entry disabled globally) */}
              {(referralInfo?.isLocked || referralInfo?.hasReferralCode) && (
              <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <Gift size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Referral Code
                  </h3>
                </div>

                {/* Show locked state if referral is locked */}
                {referralInfo?.isLocked ? (
                  <div style={{
                    padding: '16px',
                    background: '#F0FDF4',
                    border: '1px solid #A7F3D0',
                    borderRadius: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Lock size={16} color="#059669" />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>Active & Locked</span>
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      background: '#D1FAE5',
                      borderRadius: '8px',
                      marginBottom: '12px',
                    }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#065F46', fontFamily: 'monospace' }}>
                        {referralInfo.code}
                      </span>
                    </div>
                    {referralInfo.partnerName && (
                      <p style={{ fontSize: '14px', color: '#047857', margin: '0 0 8px 0' }}>
                        Referred by {referralInfo.partnerName}
                      </p>
                    )}
                    <p style={{ fontSize: '13px', color: '#065F46', margin: '0' }}>
                      Your referral code is locked because you have an active subscription.
                    </p>
                  </div>
                ) : referralInfo?.hasReferralCode ? (
                  // Show applied (unlocked) referral code — read-only (manual entry disabled globally)
                  <div style={{
                    padding: '16px',
                    background: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                  }}>
                        <div>
                            <span style={{ fontSize: '13px', color: '#6B7280' }}>Your referral code</span>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', fontFamily: 'monospace', marginTop: '4px' }}>
                              {referralInfo.code}
                            </div>
                            {referralInfo.partnerName && (
                              <span style={{ fontSize: '14px', color: '#6B7280' }}>via {referralInfo.partnerName}</span>
                            )}
                        </div>
                        <p style={{ fontSize: '13px', color: '#6B7280', margin: '12px 0 0 0' }}>
                          This code was applied via a partner link.
                        </p>
                  </div>
                ) : null}
              </div>
              )}
              {/* COMMENTED OUT — All manual referral code entry/change/remove UI disabled globally.
                   The entire unlocked Change/Remove buttons, Change Input form, and No-Code manual input
                   form have been removed. Only locked and read-only unlocked display remain.
                   To restore, see git history for the original referral section. */}

              {/* Placeholder for remaining sections - will add in next parts */}
              
              {/* Invitations Section */}
              <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <Mail size={24} color="#f9550d" />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Invitations
                  </h3>
                </div>

                {/* Send Invitation Form */}
                <div style={{ background: '#F9FAFB', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
                    Invite Family Member
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Email Input */}
                    <div style={{ minWidth: 0 }}>
                      <label htmlFor="account-invite-email" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Email Address
                      </label>
                      {/* The grey-while-sending fill is `.aps-input:disabled`
                          now, which is the same treatment every other disabled
                          field in the product has. */}
                      <TextInput
                        id="account-invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="Enter email address"
                        disabled={sendingInvite}
                        style={{
                          maxWidth: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                    </div>

                    {/* Send Button */}
                    <button
                      onClick={handleSendInvite}
                      disabled={sendingInvite || !inviteEmail.trim()}
                      style={{
                        width: '100%',
                        padding: '12px',
                        marginTop: '8px',
                        background: (sendingInvite || !inviteEmail.trim()) ? '#D1D5DB' : '#f9550d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: (sendingInvite || !inviteEmail.trim()) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                      }}
                    >
                      {sendingInvite ? (
                        <>
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail size={18} />
                          Send Invitation
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Pending Invitations List */}
                <div>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                    Pending Invitations
                  </h4>

                  {pendingInvitations && pendingInvitations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {pendingInvitations.map((invitation: any) => {
                        const isBusy = busyInviteId === invitation.id;
                        const isPending = invitation.status === 'pending';
                        const isExpired = invitation.status === 'expired';
                        // audit #178: the list endpoint now returns status='cancelled'
                        // (backed by a distinct cancelled_at), so a cancelled/resent invite
                        // is no longer conflated with a truly-accepted one. Honor either the
                        // explicit status or the cancelled_at/revoked_at signal.
                        const isCancelled =
                          invitation.status === 'cancelled' ||
                          invitation.status === 'revoked' ||
                          !!invitation.cancelled_at ||
                          !!invitation.revoked_at;
                        const isAccepted = invitation.status === 'accepted' && !isCancelled;
                        return (
                          <div
                            key={invitation.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 12,
                              padding: '12px 16px',
                              background: 'white',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 220px', minWidth: 0 }}>
                              <Mail size={18} color="#6B7280" />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {invitation.email}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                                  {isCancelled
                                    ? 'Cancelled'
                                    : isAccepted
                                      ? 'Accepted'
                                      : isExpired
                                        ? 'Expired — resend to try again'
                                        : 'Pending'}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {(isPending || isExpired) && (
                                <>
                                  <button
                                    onClick={() => handleResendInvite(invitation.id, invitation.email)}
                                    disabled={isBusy}
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: '#f9550d',
                                      background: 'white',
                                      border: '1px solid rgba(249, 85, 13, 0.5)',
                                      borderRadius: 8,
                                      cursor: isBusy ? 'not-allowed' : 'pointer',
                                      opacity: isBusy ? 0.6 : 1,
                                      fontFamily: 'Manrope, sans-serif',
                                    }}
                                  >
                                    {isBusy ? '…' : 'Resend'}
                                  </button>
                                  <button
                                    onClick={() => handleCancelInvite(invitation.id, invitation.email)}
                                    disabled={isBusy}
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: '#6B7280',
                                      background: 'white',
                                      border: '1px solid #D1D5DB',
                                      borderRadius: 8,
                                      cursor: isBusy ? 'not-allowed' : 'pointer',
                                      opacity: isBusy ? 0.6 : 1,
                                      fontFamily: 'Manrope, sans-serif',
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                              {isAccepted && (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '4px 12px',
                                  background: '#D1FAE5',
                                  color: '#065F46',
                                  borderRadius: 12,
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}>
                                  Accepted
                                </div>
                              )}
                              {isCancelled && (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '4px 12px',
                                  background: '#F3F4F6',
                                  color: '#6B7280',
                                  borderRadius: 12,
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}>
                                  Cancelled
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '32px 20px',
                        background: '#F9FAFB',
                        border: '1px dashed #D1D5DB',
                        borderRadius: '8px',
                      }}
                    >
                      <Mail size={32} color="#9CA3AF" style={{ margin: '0 auto 12px' }} />
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>
                        No Pending Invitations
                      </div>
                      <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                        Send an invitation to add family members
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
      </Modal>

      {/* Processing Overlay */}
      {processing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '40px',
              borderRadius: '16px',
              textAlign: 'center',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              minWidth: '300px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '4px solid rgba(249, 85, 13, 0.2)',
                borderTop: '4px solid #f9550d',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px',
              }}
            />
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Processing your subscription...
            </div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              Please wait while we update your plan
            </div>
          </div>
        </div>
      )}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={contactSupportModalOpen}
        onClose={() => setContactSupportModalOpen(false)}
      />

      {/* Notification Preferences Modal */}
      <NotificationPreferencesModal
        isOpen={notificationPreferencesModalOpen}
        onClose={() => setNotificationPreferencesModalOpen(false)}
      />

      {/* Email Templates Modal */}
      <EmailTemplatesModal
        isOpen={emailTemplatesModalOpen}
        onClose={() => setEmailTemplatesModalOpen(false)}
      />

      {/* X Tag Lists Modal */}
      <XTagListsModal
        isOpen={xTagListsModalOpen}
        onClose={() => setXTagListsModalOpen(false)}
      />

      {/* Delete Account Confirmation Modal */}
      {deleteConfirmModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: isMobile ? '0' : '20px',
          }}
          onClick={() => !isDeleting && setDeleteConfirmModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: isMobile ? '0' : '16px',
              maxWidth: isMobile ? '100%' : '500px',
              width: '100%',
              height: isMobile ? '100%' : 'auto',
              maxHeight: isMobile ? '100%' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: isMobile ? '24px 16px' : '32px', flex: 1, overflowY: 'auto' }}>
              {/* Icon */}
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#FEE2E2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <AlertCircle size={32} color="#DC2626" />
              </div>

              {/* Title */}
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937', textAlign: 'center', marginBottom: '12px' }}>
                Delete Your Account?
              </h2>

              {/* Warning */}
              <p style={{ fontSize: '16px', color: '#6B7280', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' }}>
                This action will schedule your account for permanent deletion in <strong>30 days</strong>.
              </p>

              {/* What will be deleted */}
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FEE2E2',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#991B1B', marginBottom: '12px' }}>
                  The following will be deleted:
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#6B7280', fontSize: '14px', lineHeight: '1.8' }}>
                  <li>Your profile and account information</li>
                  <li>All athlete profiles in your family</li>
                  <li>Messages and conversations</li>
                  <li>School lists and preferences</li>
                  <li>All uploaded documents and media</li>
                </ul>
              </div>

              {/* Subscription Warning - only show if active subscription */}
              {subscriptionData?.status === 'active' && (
                <div
                  style={{
                    background: '#FEF3C7',
                    border: '1px solid #F59E0B',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                    display: 'flex',
                    gap: '12px',
                  }}
                >
                  <div style={{ flexShrink: 0, marginTop: '2px' }}>
                    <AlertCircle size={20} color="#D97706" />
                  </div>
                  <div style={{ fontSize: '14px', color: '#92400E', lineHeight: '1.5' }}>
                    <strong>Active Subscription</strong>
                    <br />
                    {subscriptionData.store === 'app_store' ? (
                      <>
                        Your subscription will continue to bill through the App Store. Please cancel your subscription in your Apple ID settings before deleting your account.
                        <br />
                        <a 
                          href="https://apps.apple.com/account/subscriptions" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#D97706', fontWeight: '600', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}
                        >
                          Manage in App Store <ExternalLink size={14} />
                        </a>
                      </>
                    ) : subscriptionData.store === 'play_store' ? (
                      <>
                        Your subscription will continue to bill through Google Play. Please cancel your subscription in your Google Play settings before deleting your account.
                        <br />
                        <a 
                          href="https://play.google.com/store/account/subscriptions" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#D97706', fontWeight: '600', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}
                        >
                          Manage in Google Play <ExternalLink size={14} />
                        </a>
                      </>
                    ) : subscriptionData.store === 'rc_billing' || subscriptionData.store === 'stripe' ? (
                      <>
                        Your subscription will continue to bill until cancelled. Please cancel your subscription before deleting your account.
                        <br />
                        <a 
                          href={manageSubscriptionUrl(subscriptionData, user?.email)}
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#D97706', fontWeight: '600', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}
                        >
                          Manage <ExternalLink size={14} />
                        </a>
                      </>
                    ) : (
                      <>Your promotional access will be removed when your account is permanently deleted after 30 days.</>
                    )}
                  </div>
                </div>
              )}

              {/* Info box */}
              <div
                style={{
                  background: '#DBEAFE',
                  border: '1px solid #BFDBFE',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  gap: '12px',
                }}
              >
                <div style={{ flexShrink: 0, marginTop: '2px' }}>
                  <AlertCircle size={20} color="#1D4ED8" />
                </div>
                <div style={{ fontSize: '14px', color: '#1E40AF', lineHeight: '1.5' }}>
                  <strong>You can reactivate within 30 days</strong> by simply logging in again.
                  After 30 days, all data will be permanently deleted.
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setDeleteConfirmModalOpen(false)}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#DC2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    opacity: isDeleting ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {isDeleting ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                      Deleting...
                    </>
                  ) : (
                    'Yes, Delete My Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promo Cancellation Confirmation Modal */}
      {promoCancelConfirmModalOpen && pendingPromoRedemption && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '20px',
          }}
          onClick={() => !processing && setPromoCancelConfirmModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '32px' }}>
              {/* Icon */}
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#FEF3C7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <AlertCircle size={32} color="#D97706" />
              </div>

              {/* Title */}
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937', textAlign: 'center', marginBottom: '12px' }}>
                Switch to Promotional Access?
              </h2>

              {/* Warning */}
              <p style={{ fontSize: '16px', color: '#6B7280', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' }}>
                You have an active <strong>APS Membership</strong> subscription through{' '}
                <strong>
                  {pendingPromoRedemption.currentSubscription.store === 'play_store' ? 'Google Play' : 
                   pendingPromoRedemption.currentSubscription.store === 'app_store' ? 'Apple App Store' : 
                   'Web Billing'}
                </strong>.
              </p>

              {/* What will happen */}
              <div
                style={{
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400E', marginBottom: '12px' }}>
                  {pendingPromoRedemption.confirmationMessage}
                </h4>
                {pendingPromoRedemption.currentSubscription.store === 'play_store' && (
                  <p style={{ fontSize: '14px', color: '#92400E', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={14} color="#92400E" aria-label="Confirmed" style={{ flexShrink: 0 }} />
                    Your subscription will be cancelled and refunded automatically.
                  </p>
                )}
                {pendingPromoRedemption.currentSubscription.store === 'app_store' && (
                  <p style={{ fontSize: '14px', color: '#92400E', margin: 0 }}>
                    Your subscription will be cancelled. You must request a refund directly from Apple.
                  </p>
                )}
                {pendingPromoRedemption.currentSubscription.store === 'rc_billing' && (
                  <p style={{ fontSize: '14px', color: '#92400E', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Check size={14} color="#92400E" aria-label="Confirmed" style={{ flexShrink: 0 }} />
                    Your subscription will be cancelled. You may be eligible for a prorated refund.
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setPromoCancelConfirmModalOpen(false);
                    setPendingPromoRedemption(null);
                  }}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    opacity: processing ? 0.5 : 1,
                  }}
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleConfirmPromoRedemption}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#5046e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    opacity: processing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {processing ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}
                      />
                      Processing...
                    </>
                  ) : (
                    'Cancel & Use Promo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Management Portal Confirmation Modal */}
      {managePortalConfirmOpen && pendingManagePortal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            padding: '20px',
          }}
          onClick={() => {
            setManagePortalConfirmOpen(false);
            setPendingManagePortal(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                Manage Your Subscription
              </h3>
              <button
                onClick={() => {
                  setManagePortalConfirmOpen(false);
                  setPendingManagePortal(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              <div
                style={{
                  background: '#EEF2FF',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="#6366F1"/>
                  </svg>
                  <div>
                    <p style={{ fontSize: '14px', color: '#4338CA', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                      {pendingManagePortal.action === 'promo' ? (
                        <>
                          To use a promo code, you'll need to first cancel your current subscription in the billing portal.
                          <br /><br />
                          After cancelling, return here to apply your promo code.
                        </>
                      ) : (
                        // 'team_covered': prepare-purchase's own explanation (your team
                        // includes the membership; cancel the store subscription to stop paying).
                        pendingManagePortal.message
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Current Plan Info */}
              <div
                style={{
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '24px',
                }}
              >
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                  Current Plan: <span style={{ color: '#111827', fontWeight: '500' }}>{plans?.[0]?.name || 'APS Membership'}</span>
                </p>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setManagePortalConfirmOpen(false);
                    setPendingManagePortal(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    window.open(pendingManagePortal.managementUrl, '_blank');
                    setManagePortalConfirmOpen(false);
                    setPendingManagePortal(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: '#5046e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  Manage
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel invitation confirmation (replaces the OS window.confirm
          dialog so it matches the rest of the app's branded modals). */}
      <ConfirmDialog
        isOpen={!!pendingCancelInvite}
        onClose={() => setPendingCancelInvite(null)}
        onConfirm={confirmCancelInvite}
        title="Cancel invitation?"
        message={pendingCancelInvite
          ? `${pendingCancelInvite.email} won't be able to join from this invitation. You can always send a new one.`
          : ''}
        confirmText="Cancel invitation"
        cancelText="Keep"
        variant="danger"
        isLoading={!!pendingCancelInvite && busyInviteId === pendingCancelInvite.id}
      />

      {/* Remove family member confirmation. */}
      <ConfirmDialog
        isOpen={!!pendingRemoveMember}
        onClose={() => setPendingRemoveMember(null)}
        onConfirm={async () => {
          if (!pendingRemoveMember) return;
          const { id, email } = pendingRemoveMember;
          try {
            await removeMember(id);
            toast.success(`Removed ${email} from the family`);
            setMemberDetailsModalOpen(false);
            setPendingRemoveMember(null);
            await loadFamily();
          } catch (err: any) {
            toast.error(err.message || 'Failed to remove family member');
            setPendingRemoveMember(null);
          }
        }}
        title="Remove from family?"
        message={pendingRemoveMember
          ? `${pendingRemoveMember.email} will lose access to your shared family data. Their account stays — you can re-invite them later.`
          : ''}
        confirmText="Remove"
        cancelText="Keep"
        variant="danger"
      />

      {/* Member Details Modal */}
      {memberDetailsModalOpen && selectedMember && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '20px',
          }}
          onClick={() => setMemberDetailsModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                {selectedMember.type === 'athlete' ? 'Athlete Profile' : 'Non-Athlete Profile'}
              </h2>
              <button
                onClick={() => setMemberDetailsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6B7280',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#1F2937')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              {/* Basic Information Section */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <User size={20} color="#f9550d" />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Basic Information
                  </h3>
                </div>

                <div
                  style={{
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '20px',
                  }}
                >
                  {/* Name with Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <span style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937' }}>
                      {selectedMember.first_name} {selectedMember.last_name}
                    </span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        background: selectedMember.type === 'athlete' ? '#ECFDF5' : '#EFF6FF',
                        color: selectedMember.type === 'athlete' ? '#059669' : '#3B82F6',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600',
                      }}
                    >
                      {selectedMember.type === 'athlete' ? <Zap size={14} /> : <Shield size={14} />}
                      {selectedMember.type === 'athlete' ? 'Athlete' : 'Non-Athlete'}
                    </span>
                  </div>

                  {/* Email */}
                  {selectedMember.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <Mail size={16} color="#6B7280" />
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Email:</span>
                      <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.email}</span>
                    </div>
                  )}

                  {/* Phone */}
                  {selectedMember.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <Phone size={16} color="#6B7280" />
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Phone:</span>
                      <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.phone}</span>
                    </div>
                  )}

                  {/* Athlete-specific info */}
                  {selectedMember.type === 'athlete' && (
                    <>
                      {selectedMember.sport && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <Activity size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Sport:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.sport}</span>
                        </div>
                      )}

                      {selectedMember.graduation_year && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <Calendar size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Graduation Year:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.graduation_year}</span>
                        </div>
                      )}

                      {selectedMember.position && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <Target size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Position:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.position}</span>
                        </div>
                      )}

                      {selectedMember.city && selectedMember.state && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <MapPin size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Location:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>
                            {selectedMember.city}, {selectedMember.state}
                          </span>
                        </div>
                      )}

                      {selectedMember.date_of_birth && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0' }}>
                          <Calendar size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Date of Birth:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>
                            {(() => { const [y, m, d] = selectedMember.date_of_birth.substring(0, 10).split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(); })()}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Guardian-specific info */}
                  {selectedMember.type === 'guardian' && selectedMember.relationship_to_athlete && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0' }}>
                      <Heart size={16} color="#6B7280" />
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Relationship:</span>
                      <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{selectedMember.relationship_to_athlete}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Connected Accounts Section */}
              {selectedMember.linked_users && selectedMember.linked_users.length > 0 && (
                <div style={{ marginBottom: '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <LinkIcon size={20} color="#f9550d" />
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                      Connected Accounts
                    </h3>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selectedMember.linked_users.map((linkedUser: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          background: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          padding: '16px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <User size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Email:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>{linkedUser.email}</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: linkedUser.is_billing_owner ? '12px' : '0' }}>
                          <Calendar size={16} color="#6B7280" />
                          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Joined:</span>
                          <span style={{ fontSize: '14px', color: '#1F2937', flex: 1 }}>
                            {new Date(linkedUser.joined_at).toLocaleDateString()}
                          </span>
                        </div>

                        {linkedUser.is_billing_owner && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <CreditCard size={16} color="#f9550d" />
                            <span style={{ fontSize: '14px', color: '#f9550d', fontWeight: '600' }}>
                              Billing Owner
                            </span>
                          </div>
                        )}

                        {/* Remove from family — billing owner only, and only
                            for non-self / non-billing-owner targets. The
                            target user keeps their account; we just unlink
                            them from this family so they can be re-invited
                            or join a different one. */}
                        {!linkedUser.is_billing_owner
                          && userProfile?.is_billing_owner
                          && linkedUser.id !== userProfile?.id && (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setPendingRemoveMember({ id: linkedUser.id, email: linkedUser.email })}
                              style={{
                                padding: '8px 16px',
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#B91C1C',
                                background: 'white',
                                border: '1px solid #FCA5A5',
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontFamily: 'Manrope, sans-serif',
                              }}
                            >
                              Remove from family
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSettingsModal;
